import type { ReactNode } from "react";
import { cn } from "../cn.js";
import { AlertCircle, CheckCircle2, Info, X } from "../icons.js";

export type ToastTone = "success" | "error" | "info";

interface ToastProps {
  tone: ToastTone;
  message: ReactNode;
  action?: { label: string; onClick: () => void } | undefined;
  onDismiss?: () => void;
}

const toneClass: Record<ToastTone, string> = {
  success: "border-green-500/40 text-green-700 dark:text-green-300",
  error: "border-destructive/50 text-destructive",
  info: "border-input text-foreground",
};

const ToneIcon = { success: CheckCircle2, error: AlertCircle, info: Info } as const;

/** A single presentational toast card. State/timing live in the caller. */
export function Toast({ tone, message, action, onDismiss }: ToastProps) {
  const Icon = ToneIcon[tone];
  return (
    <div
      role="status"
      className={cn(
        "pointer-events-auto flex items-start gap-2 rounded-md border bg-card px-3 py-2 text-sm shadow-lg",
        toneClass[tone],
      )}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1 text-foreground">{message}</div>
      {action ? (
        <button
          type="button"
          onClick={action.onClick}
          className="shrink-0 self-center rounded border px-2 py-0.5 text-xs font-medium text-foreground hover:bg-accent"
        >
          {action.label}
        </button>
      ) : null}
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}

/** Fixed container that stacks toasts. Mount once near the app root. */
export function ToastViewport({ children }: { children: ReactNode }) {
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2"
    >
      {children}
    </div>
  );
}
